/**
 * This component glues together a TextInput with a dropdown menu of suggestions. It was built for the
 * Catalog # input field in `IngredientTable`. We will likely be replacing it with https://react-select.com/,
 * but are postponing that work for the moment in order to ship the feature sooner. That's why this file
 * lives under client/registry/mixtures and has no unit tests of its own.
 */
import React from 'react';
import ReactDOM from 'react-dom';
import isEqual from 'react-fast-compare';

import Dropdown from 'ui-library/dropdowns/Dropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import TextInput, {TextInputProps} from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth, OptionData, OptionValue} from 'ui-library/menus/MenuConstants';
import {
  defaultRenderOptionContent,
  getMatchingOptionDatas,
  getOptionRefForIndex,
  getSelectableIndices,
  hoverToSelectableIndex,
} from 'ui-library/menus/MenuUtil';
import OptionContainer from 'ui-library/menus/OptionContainer';
import withMenuNavigationLoading, {ConnectedMenuProps} from 'ui-library/menus/withMenuNavigationLoading';
import * as keyboard from 'util/Keyboard';
import {verticallyScrollItemIntoView} from 'util/Scroll';
import {ElementKeyboardEvent} from 'util/Types';

interface Props extends TextInputProps {
  getMatchingOptionDatas?: (query: string, optionDatas: Array<OptionData>) => Array<OptionData>;
  isLoading?: boolean;
  onChange?: (e: {target: {value: string}}) => void;
  optionDatas: Array<OptionData>;
  value: string;
}

interface State {
  isMenuOpen: boolean;
  menuItems: Array<OptionData>;
  value: string;
}

function isElement(element: Element | Text | null): element is Element {
  return Boolean(element && (element as Element).id != null);
}

export class _TextInputWithSuggestions extends React.Component<Props & ConnectedMenuProps, State> {
  static defaultProps: Partial<Props> = {
    getMatchingOptionDatas,
  };

  // These refs give us access to DOM nodes, which we need so we can get placement and size info for
  // scrolling. See `scrollIndexIntoView` below
  private optionRefs: {[ref: string]: OptionContainer<OptionValue> | null} = {};

  override state: State = {value: '', isMenuOpen: false, menuItems: []};

  scrollAreaRef = React.createRef<Menu>();

  override componentDidMount(): void {
    this.updateState({value: this.props.value, isMenuOpen: false, forceUpdateSelectableIndices: true});
  }

  override componentDidUpdate(
    prevProps: Readonly<Props & ConnectedMenuProps>,
    prevState: Readonly<State>
  ): void {
    if (prevProps.isLoading && !this.props.isLoading) {
      this.updateState({value: this.state.value, isMenuOpen: this.state.isMenuOpen});
    }
  }

  private scrollIndexIntoView = (hoverIndex: number): void => {
    const scrollArea = ReactDOM.findDOMNode(this.scrollAreaRef.current);
    const itemBox = ReactDOM.findDOMNode(this.optionRefs[getOptionRefForIndex(hoverIndex)]);
    if (isElement(scrollArea) && isElement(itemBox)) {
      verticallyScrollItemIntoView(scrollArea, itemBox);
    }
  };

  private changeHovered = (newHoverIndex: number): void => {
    const clampedHoverIndex = this.props.onChangeHoverIndex(newHoverIndex);
    this.scrollIndexIntoView(clampedHoverIndex);
  };

  private updateState = ({
    value,
    isMenuOpen,
    forceUpdateSelectableIndices,
  }: {
    value: string;
    isMenuOpen: boolean;
    forceUpdateSelectableIndices?: boolean;
  }): void => {
    const menuItems = this.getMenuItems(value);
    const selectableIndices = getSelectableIndices(menuItems);
    const selectableIndicesChanged = !isEqual(selectableIndices, this.props.selectableIndices);
    const menuItemsChanged = !isEqual(
      menuItems.map((item) => item.pureTextName ?? item.name),
      this.state.menuItems.map((item) => item.pureTextName ?? item.name)
    );

    if (value !== this.state.value && this.props.onChange) {
      this.props.onChange({target: {value}});
    }

    if (selectableIndicesChanged || forceUpdateSelectableIndices) {
      this.props.onUpdateSelectableIndices(selectableIndices);
    }

    if (menuItemsChanged || value !== this.state.value || isMenuOpen !== this.state.isMenuOpen) {
      this.setState({menuItems, value, isMenuOpen});
    }
  };

  private getMenuItems = (value?: string): Array<OptionData> =>
    this.props.isLoading
      ? [{name: <HorizontalThrobber />, disabled: true, pureTextName: 'Loading...'}]
      : this.props.getMatchingOptionDatas!(value ?? this.state.value, this.props.optionDatas);

  private handleKeyDown = (e: ElementKeyboardEvent): boolean => {
    const {hoverIndex} = this.props;
    let shouldCallPropsOnKeyDown = true;
    if (keyboard.isUpKey(e)) {
      this.changeHovered(hoverIndex - 1);
      e.preventDefault(); // Prevent up from moving text cursor to the beginning of line
    } else if (keyboard.isDownKey(e)) {
      if (this.state.isMenuOpen) {
        this.changeHovered(hoverIndex + 1);
      } else {
        this.setState({isMenuOpen: true});
        this.changeHovered(0);
      }
      e.preventDefault(); // Prevent dropdown toggle from also handling this event
    } else if (keyboard.isEnterKey(e)) {
      shouldCallPropsOnKeyDown = !this.state.isMenuOpen;
      const newValue =
        this.props.getMatchingOptionDatas!(this.state.value, this.props.optionDatas)[
          hoverToSelectableIndex(this.props.selectableIndices, hoverIndex)
        ]?.value ?? this.state.value;
      this.updateState({value: newValue, isMenuOpen: false});
      if (this.state.isMenuOpen) {
        e.preventDefault(); // Prevent default form submission behavior when menu is open
      }
    }

    return shouldCallPropsOnKeyDown;
  };

  override render(): JSX.Element {
    const menuItems = this.getMenuItems();
    const menu = (
      <Menu
        width={MenuWidth.MEDIUM}
        height={MenuHeight.MEDIUM}
        ref={this.scrollAreaRef}
        disableKeyboardAccess={true}
      >
        {menuItems.map((option, index) => (
          <OptionContainer
            key={index}
            value={option.value}
            isHovered={index === hoverToSelectableIndex(this.props.selectableIndices, this.props.hoverIndex)}
            onHover={() => {
              this.props.onHoverOption(index);
            }}
            onSelect={(optionValue: string) => {
              this.updateState({value: optionValue, isMenuOpen: false});
            }}
            ref={(node) => {
              this.optionRefs[getOptionRefForIndex(index)] = node;
            }}
          >
            {defaultRenderOptionContent(option)}
          </OptionContainer>
        ))}
      </Menu>
    );

    return (
      <Dropdown menu={menu} isOpen={this.state.isMenuOpen && Boolean(menuItems.length)} alwaysDropDown={true}>
        <TextInput
          className={this.props.className}
          hasButton={this.props.hasButton}
          hasError={this.props.hasError}
          size={this.props.size}
          type={this.props.type}
          autoFocus={this.props.autoFocus}
          disabled={this.props.disabled}
          readOnly={this.props.readOnly}
          value={this.state.value}
          onChange={(e) => {
            this.updateState({value: e.target.value, isMenuOpen: true});
          }}
          onFocus={(e) => {
            this.updateState({value: this.state.value, isMenuOpen: true});
          }}
          onBlur={(e) => {
            this.updateState({value: this.state.value, isMenuOpen: false});
          }}
          onKeyDown={(e) => {
            const shouldCallPropsOnKeyDown = this.handleKeyDown(e);
            if (shouldCallPropsOnKeyDown && this.props.onKeyDown) {
              this.props.onKeyDown(e);
            }
          }}
          dataTestAttrs={this.props.dataTestAttrs}
        />
      </Dropdown>
    );
  }
}

const TextInputWithSuggestions = withMenuNavigationLoading(_TextInputWithSuggestions);
export default TextInputWithSuggestions;
